import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  TextField,
  IconButton,
  Box,
  Button,
  useTheme,
  InputAdornment,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import ReplayIcon from '@material-ui/icons/Replay';
import SearchIcon from '@material-ui/icons/Search';
import { useDispatch, useSelector } from 'dva';
import { useIntl } from 'umi';
import DataTableNativeML from '@/components/DataTableNativeML';
import SvgIconML from '@/components/SvgIconML/SvgIconML';
import CouponModalStyled from './CouponModalStyled';

export default (props) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const theme = useTheme();
  const dispatch = useDispatch();
  const loadingEffect = useSelector((state) => state.loading);
  const couponLoading = loadingEffect.effects['cpList/getCpList'];

  const { open, onClose, onConfirm, defaultCouponList = [] } = props;

  const [allCpList, setAllCpList] = useState([]);
  const [defaultCouponListState, setDefaultCouponListState] = useState(
    defaultCouponList || [],
  );
  const [selectedCouponList, setSelectedCouponList] = useState(
    defaultCouponList || [],
  );
  const [couponTitle, setCouponTitle] = useState('');
  const [couponId, setCouponId] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [pageData, setPageData] = useState({
    pageSize: 8,
    pageNum: 1,
  });

  const couponIdList = useMemo(
    () => (defaultCouponList || []).map((i) => i.id),
    [defaultCouponList],
  );

  const fetchCouponList = useCallback(() => {
    dispatch({
      type: 'cpList/getCpList',
      payload: {
        values: {
          pageNum: pageData.pageNum,
          pageSize: pageData.pageSize,
          status: 'NOT_COMPLETE',
          title: isSearch ? couponTitle : null,
          id: isSearch ? couponId : null,
        },
      },
    });
  }, [dispatch, pageData, isSearch, couponTitle, couponId, refreshKey]);

  useEffect(() => {
    if (open) {
      fetchCouponList();
    }
  }, [fetchCouponList, open]);

  const {
    data: cpList,
    total,
    pages,
  } = useSelector((state) => state.cpList?.cpList) || {};

  useEffect(() => {
    if (cpList?.length > 0) {
      setAllCpList((prev) => {
        const map = new Map();
        [...prev, ...cpList].forEach((item) => map.set(item.id, item));
        return Array.from(map.values());
      });
    }
  }, [cpList]);

  useEffect(() => {
    if (open) {
      setDefaultCouponListState(defaultCouponList || []);
      setSelectedCouponList(defaultCouponList || []);
    } else {
      setAllCpList([]);
    }
  }, [open, defaultCouponList]);

  const handleClose = () => {
    onClose && onClose();
  };

  const handleReset = () => {
    setIsSearch(false);
    setCouponTitle('');
    setCouponId('');
    setPageData({ pageSize: 8, pageNum: 1 });
    setRefreshKey((k) => k + 1);
  };

  const handleSearch = () => {
    setPageData({ ...pageData, pageNum: 1 });
    setIsSearch(true);
    setRefreshKey((k) => k + 1);
  };

  const onSubmit = () => {
    const result = (selectedCouponList || []).map((item) => ({
      ...item,
      name: item.title,
    }));
    onConfirm && onConfirm(result);
    handleClose();
  };

  const changePage = (obj) => {
    setPageData({ ...pageData, ...obj });
  };

  const handleCheckboxChange = (a, b, c) => {
    const currentId = b && b.id;
    const copyDefaultCouponList = [...defaultCouponListState];
    if (currentId != null) {
      const index = copyDefaultCouponList.findIndex((i) => i.id === currentId);
      if (index >= 0) {
        copyDefaultCouponList.splice(index, 1);
        setDefaultCouponListState([...copyDefaultCouponList]);
      }
    }
    const uAllCpList = Array.from(
      new Map(allCpList.map((item) => [item.id, item])).values(),
    );
    const z = [];
    for (let i = 0; i < (c || []).length; i++) {
      const cpItem = uAllCpList.find((item) => item.id === c[i].id);
      if (cpItem) {
        z.push(cpItem);
      }
    }
    const t = [...z, ...copyDefaultCouponList];
    const seen = new Set();
    const array = t.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
    setSelectedCouponList(array);
  };

  const columns = [
    {
      type: 'checkbox',
      width: 80,
      onChange: handleCheckboxChange,
    },
    {
      title: '优惠券ID',
      align: 'center',
      id: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '优惠券名称',
      align: 'center',
      id: 'title',
      key: 'title',
    },
    {
      title: '优惠内容',
      align: 'center',
      id: 'subTitle',
      key: 'subTitle',
      width: 220,
    },
    {
      title: '剩余库存',
      align: 'center',
      id: 'surplus',
      key: 'surplus',
      width: 100,
    },
  ];

  return (
    <CouponModalStyled
      open={open}
      onClose={handleClose}
      aria-labelledby="coupon-modal-title"
      theme={theme}
    >
      <Box className="paper">
        <Box className="first_line">
          <div className="title">选择优惠券</div>
          <div className="close">
            <SvgIconML
              size={20}
              name={'modalClose'}
              onClick={handleClose}
              fill="#212121"
            />
          </div>
        </Box>
        <Box className="coupon-name-wrap">
          <TextField
            className="coupon-name"
            variant="standard"
            value={couponTitle}
            onChange={(e) => {
              setCouponTitle(e.target.value);
              setIsSearch(false);
            }}
            label="优惠券名称"
            placeholder="请输入优惠券名称"
            InputProps={{
              endAdornment: couponTitle ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setCouponTitle('');
                      setIsSearch(false);
                    }}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TextField
            className="coupon-name"
            variant="standard"
            value={couponId}
            onChange={(e) => {
              setCouponId(e.target.value);
              setIsSearch(false);
            }}
            label="优惠券ID"
            placeholder="请输入优惠券ID"
            InputProps={{
              endAdornment: couponId ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setCouponId('');
                      setIsSearch(false);
                    }}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Box className="button-group">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleReset}
              startIcon={<ReplayIcon sx={{ width: '16px', height: '16px' }} />}
            >
              {formatMessage({ id: 'comm.action.btn.06' })}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon sx={{ width: '16px', height: '16px' }} />}
            >
              {formatMessage({ id: 'comm.action.btn.01' })}
            </Button>
          </Box>
        </Box>
        <Box className="coupon-list">
          <DataTableNativeML
            loading={couponLoading}
            columns={columns}
            changePage={changePage}
            page={pageData}
            data={cpList}
            total={total}
            pages={pages}
            chooseIds={couponIdList}
            columnKey="id"
            paging={true}
            showPageColor={true}
            np={true}
          />
        </Box>
        <div className="modal-btn-wrap">
          <Button onClick={handleClose} variant="contained" color="secondary">
            {formatMessage({ id: 'comm.action.btn.08' })}
          </Button>
          <Button
            variant="contained"
            className="btnSubmit button"
            onClick={onSubmit}
          >
            {formatMessage({ id: 'comm.action.btn.07' })}
          </Button>
        </div>
      </Box>
    </CouponModalStyled>
  );
};
